import type { WorkspaceMember } from "../api/types";

/**
 * @-mention composer logic (pure, unit-tested). Detects the active `@query`
 * under the caret so the combobox can open, and rewrites the composer text when
 * a member is picked. Multi-word display names are inserted whole (`@Kai Brandt `)
 * even though the typed query is a single token.
 */

export interface MentionQuery {
  /** Index of the `@` that opened the query. */
  readonly start: number;
  /** The text typed after `@`, up to the caret (no whitespace). */
  readonly query: string;
}

/** The active `@…` mention query under `caret`, or null. A mention opens on an
 *  `@` at the start of input or after whitespace, and runs to the caret with no
 *  intervening whitespace or second `@`. */
export function activeMentionQuery(value: string, caret: number): MentionQuery | null {
  let i = caret - 1;
  while (i >= 0) {
    const ch = value[i];
    if (ch === "@") {
      const before = i === 0 ? " " : value[i - 1];
      if (/\s/.test(before)) return { start: i, query: value.slice(i + 1, caret) };
      return null;
    }
    if (ch === undefined || /\s/.test(ch)) return null;
    i -= 1;
  }
  return null;
}

/** Rewrite `value` replacing the active `@query` with `@<name> ` and return the
 *  new value + the caret position just after the inserted mention. */
export function insertMention(
  value: string,
  mq: MentionQuery,
  name: string,
): { value: string; caret: number } {
  const before = value.slice(0, mq.start);
  const after = value.slice(mq.start + 1 + mq.query.length);
  const token = `@${name} `;
  return { value: before + token + after, caret: (before + token).length };
}

/** Filter workspace members by a mention query (case-insensitive, name OR email
 *  substring). An empty query returns the first `limit` members. */
export function filterMembers(
  members: WorkspaceMember[],
  query: string,
  limit = 8,
): WorkspaceMember[] {
  const q = query.trim().toLowerCase();
  const hits = q === ""
    ? members
    : members.filter(
        (m) =>
          m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
      );
  return hits.slice(0, limit);
}

/** Derive the mention user-ids to send from the FINAL composer text: a member
 *  is mentioned iff `@<name>` still appears in the body (so deleting the token
 *  drops the mention). This keeps the composer from having to track state. */
export function deriveMentions(body: string, members: WorkspaceMember[]): number[] {
  const ids: number[] = [];
  for (const m of members) {
    if (body.includes(`@${m.name}`) && !ids.includes(m.id)) ids.push(m.id);
  }
  return ids;
}
